import { ChangeEvent } from 'react'

import { IconPlus, IconTrash } from '@tabler/icons-react'
import { Row, Col, Input, Menu } from 'antd'
import { Rule } from 'antd/lib/form'
import moment from 'moment-timezone'

import { TimeNotesCaptureBlock as TimeNotesCaptureBlockType } from '../../types/blueprint'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'
import { RangePickerWithType, PickerWithType } from './DateTimeCaptureBlock'

import { Button } from '@/components/shared-components/BUI/Button'
import { Text } from '@/components/shared-components/BUI/Text'
import Ellipsis from '@/components/shared-components/EllipsisDropdown'
import { Flexbox } from '@/components/shared-components/Flexbox'

type Value = TimeNotesCaptureBlockType['value']
type PickerType = TimeNotesCaptureBlockType['pickerType']

type TimeNotesProps = {
  onChange?: (value: Value) => void
  value?: Value
  captureRange: TimeNotesCaptureBlockType['captureRange']
  pickerType: PickerType
}

const ellipsisMenu = (onDelete) => (
  <Menu>
    <Menu.Item
      danger
      icon={<IconTrash size={16} />}
      onClick={onDelete}
      key="remove"
    >
      Remove
    </Menu.Item>
  </Menu>
)

export const getDiffPeriod = (pickerType: PickerType) => {
  switch (pickerType) {
    case 'date':
    case 'dateTime':
      return 'days'
    case 'month':
      return 'months'
    case 'time':
      return 'hours'
    case 'week':
    case 'quarter':
      return 'weeks'
    case 'year':
      return 'years'
  }
}

const getRangeValue = (
  dateTime: string | string[]
): [moment.Moment, moment.Moment] => {
  return Array.isArray(dateTime)
    ? [moment(dateTime[0]), moment(dateTime[1])]
    : [moment(dateTime), moment(dateTime)]
}

export const getDuration = (value: Value, pickerType: PickerType) => {
  let duration = 0

  for (const timeNote of value) {
    if (Array.isArray(timeNote.dateTime)) {
      const diff = moment(timeNote.dateTime[1]).diff(
        timeNote.dateTime[0],
        getDiffPeriod(pickerType),
        true
      )

      duration += diff
    }
  }

  return Math.round(duration * 100) / 100
}

export const TimeNotes: React.FC<TimeNotesProps> = ({
  onChange,
  value = [],
  pickerType,
  captureRange,
}) => {
  const cloneValue = () => {
    return value.map((timeNote) => ({ ...timeNote }))
  }

  const setTime = (index: number) => (date) => {
    const newValue = cloneValue()
    newValue[index].dateTime = date === null ? undefined : date
    onChange?.(newValue)
  }

  const setNote = (index: number) => (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = cloneValue()
    newValue[index].note = e.target.value
    onChange?.(newValue)
  }

  const deleteTimeNote = (index: number) => () => {
    const newValue = cloneValue()
    newValue.splice(index, 1)
    onChange?.(newValue)
  }

  const addTimeNote = () => {
    const time = moment().toString()
    const newValue = cloneValue()

    newValue.push({
      dateTime: [time, time],
      note: '',
    } as Value[number])
    onChange?.(newValue)
  }

  return (
    <div>
      {value?.map((timeNote, index) => (
        <Row
          key={`${index}-${timeNote.dateTime?.toLocaleString()}`}
          gutter={[24, 12]}
          align="middle"
          style={{ marginTop: '1rem' }}
        >
          <Col xs={24} md={10}>
            {captureRange ? (
              <RangePickerWithType
                type={pickerType}
                value={getRangeValue(timeNote.dateTime)}
                onChange={setTime(index)}
              />
            ) : (
              <PickerWithType
                type={pickerType}
                value={moment(timeNote.dateTime)}
                onChange={setTime(index)}
              />
            )}
          </Col>

          <Col xs={22} md={12}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 10 }}
              value={timeNote.note}
              onChange={setNote(index)}
              placeholder="Comments"
            />
          </Col>

          <Col xs={2}>
            <Ellipsis menu={ellipsisMenu(deleteTimeNote(index))} />
          </Col>
        </Row>
      ))}

      <br />
      <Flexbox justifyContent="space-between" alignItems="center" gap="1rem">
        <Button onClick={addTimeNote} icon={<IconPlus size={16} />}>
          Add time
        </Button>
        <Text secondary="_600">
          Total {getDiffPeriod(pickerType)}: {getDuration(value, pickerType)}
        </Text>
      </Flexbox>
    </div>
  )
}

type Props = {
  block: TimeNotesCaptureBlockType
  rules: Rule[]
}

const getInitialDateTime = (
  dateTime: string | string[],
  caputerRange: boolean
) => {
  if (caputerRange) {
    return Array.isArray(dateTime)
      ? [moment(dateTime[0]), moment(dateTime[1])]
      : [moment(dateTime)]
  }

  return Array.isArray(dateTime) ? moment(dateTime[0]) : moment(dateTime)
}

export const TimeNotesCaptureBlock: React.FC<Props> = ({ block, rules }) => {
  const { value, captureRange, pickerType } = block

  const initialValue = value?.map((timeNote) => ({
    ...timeNote,
    dateTime: getInitialDateTime(timeNote?.dateTime, captureRange),
  }))

  return (
    <CaptureBlockFormItem
      initialValue={initialValue}
      rules={rules}
      block={block}
    >
      <TimeNotes captureRange={captureRange} pickerType={pickerType} />
    </CaptureBlockFormItem>
  )
}
